import type { DataImportSource, DataImportSourceType } from "@/schema/data-import-source"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"

export interface FanOutDataImport {
  id: string
  guid: string
  book_id: string
  source_type: DataImportSourceType
  status: string
  wizard_step: string
  created_at: string
}

export interface FanOutResponse {
  data_imports: FanOutDataImport[]
}

interface SourcesResponse {
  sources: DataImportSource[]
}

export function fetchDataImportSources(context: ApiContext) {
  return apiContextFetch<SourcesResponse>(context, "data_import_sources").then((r) => r.sources)
}

export function fanOutDataImportSource(context: ApiContext, sourceId: string) {
  return apiContextFetch<FanOutResponse>(context, `data_import_sources/${sourceId}/fan_outs`, {
    method: "POST",
    body: JSON.stringify({}),
  })
}

export function submitTwoFactorCodeForSource(context: ApiContext, sourceId: string, code: string) {
  return apiContextFetch<void>(context, `data_import_sources/${sourceId}/two_factor_authentications`, {
    method: "POST",
    body: JSON.stringify({ code }),
  })
}

export function updateDataImportSourceCredentials(
  context: ApiContext,
  sourceId: string,
  credentials: { crmUsername: string; crmPassword: string }
) {
  return apiContextFetch<DataImportSource>(context, `data_import_sources/${sourceId}`, {
    method: "PATCH",
    body: JSON.stringify({
      data_import_source: {
        crm_username: credentials.crmUsername,
        crm_password: credentials.crmPassword,
      },
    }),
  })
}

export async function uploadDataImportSourceFile(context: ApiContext, file: File) {
  const apiRoot = (process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/").replace("/api/", "")
  const { uploadFileDirectly } = await import("@/utils/direct-upload")

  return uploadFileDirectly(file, apiRoot, context.token)
}

export async function createDataImportSource(
  context: ApiContext,
  sourceType: DataImportSourceType,
  file: File | null,
  getRefreshedContext?: () => Promise<ApiContext | null>,
  bookId?: string | null,
  options?: {
    importWorkflows?: boolean
    importOpportunities?: boolean
    crmUsername?: string
    crmPassword?: string
    importFileSignedId?: string
  }
) {
  // Enterprise Redtail sources can be created credentials-only; the Slant team attaches the backup later.
  const signedId = options?.importFileSignedId ?? (file ? await uploadDataImportSourceFile(context, file) : null)

  // Multi-GB uploads can take a while; refresh the token before the final POST.
  const freshContext = getRefreshedContext ? ((await getRefreshedContext()) ?? context) : context

  const sourceAttrs: {
    source_type: DataImportSourceType
    book_id?: string | null
    import_workflows?: boolean
    import_opportunities?: boolean
    crm_username?: string
    crm_password?: string
  } = { source_type: sourceType }
  if (bookId !== undefined) sourceAttrs.book_id = bookId
  if (sourceType === "wealthbox" || sourceType === "redtail") {
    sourceAttrs.import_workflows = options?.importWorkflows ?? false
    sourceAttrs.import_opportunities = options?.importOpportunities ?? false
  } else if (options?.importWorkflows) {
    sourceAttrs.import_workflows = true
  }
  // Send the Redtail login whenever it's provided — it's no longer tied to the workflows toggle.
  if (options?.crmUsername) sourceAttrs.crm_username = options.crmUsername
  if (options?.crmPassword) sourceAttrs.crm_password = options.crmPassword

  return apiContextFetch<DataImportSource>(freshContext, "data_import_sources", {
    method: "POST",
    body: JSON.stringify({
      data_import_source: sourceAttrs,
      import_files: signedId ? [signedId] : [],
    }),
  })
}
